import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { paypal as paypalApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const PayPalSuccess = () => {
  const [params] = useSearchParams();
  // PayPal returns ?token=ORDER_ID&PayerID=...
  const orderId = params.get("token");
  const [state, setState] = useState("capturing"); // capturing | paid | failed
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const captured = useRef(false);

  useEffect(() => {
    if (!orderId || captured.current) return;
    captured.current = true;

    (async () => {
      try {
        const res = await paypalApi.captureOrder(orderId);
        if (res.payment_status === "paid") {
          setState("paid");
          refresh();
        } else {
          setState("failed");
        }
      } catch {
        setState("failed");
      }
    })();
  }, [orderId, refresh]);

  return (
    <main
      data-testid="paypal-success-page"
      className="min-h-[70vh] bg-zinc-50 flex items-center justify-center p-6"
    >
      <div className="bg-white border border-zinc-200 max-w-md w-full p-10 text-center">
        {state === "capturing" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-[#4F46E5] animate-spin" />
            <h1 className="font-display font-black text-2xl mb-2">Confirmando tu pago...</h1>
            <p className="text-zinc-600 text-sm">No cierres esta ventana.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto mb-4 text-emerald-600" />
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600 mb-2">
              Pago confirmado
            </p>
            <h1 className="font-display font-black text-3xl tracking-tighter mb-3">
              ¡Bienvenido a Kursos Online!
            </h1>
            <p className="text-zinc-600 mb-6">
              Tu acceso ha sido activado. Ya puedes empezar a aprender.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="paypal-goto-dashboard-btn"
              className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white py-3.5 font-semibold"
            >
              Ir a mis cursos
            </button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-14 h-14 mx-auto mb-4 text-red-500" />
            <h1 className="font-display font-black text-2xl mb-2">No pudimos procesar el pago</h1>
            <p className="text-zinc-600 mb-6 text-sm">
              Si el dinero salió de tu cuenta, escríbenos y lo resolvemos.
            </p>
            <Link to="/" className="block w-full bg-zinc-900 text-white py-3.5 font-semibold">
              Volver al inicio
            </Link>
          </>
        )}
      </div>
    </main>
  );
};

export default PayPalSuccess;
